import React, {
	type ReactNode,
	type SyntheticEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import LightbulbIcon from '@atlaskit/icon/glyph/lightbulb';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { R300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { FetchError } from '@atlassian/jira-fetch';
import { AkAutoDismissFlag, FlagGroup } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useCurrentUser } from '@atlassian/jira-platform-services-user-current';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/jira-router';
import { ERROR_CODE_NOT_FOUND } from '@atlassian/jira-software-requests-page/src/common/constants';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import {
	type CreateJSMProjectResponseType,
	createFirstJSMProject,
} from '../../../services/create-jsm-project';
import {
	addList,
	getCompletedItem,
	markItemCompleted,
} from '../../../services/fetch-show-top-banner-details';
import AutoCreateProjectModal from './auto-create-project-modal';
import messages from './messages';

const SERVICE_DESK_AUTO_PROJECT_CREATE_BANNER = 'servicedesk-auto-project-create-banner';
const AUTO_CREATE_PROJECT_MODAL_REFERRER_VALUE = 'AutoCreateProjectModal';
export const TopBanner = () => {
	const { push } = useRouterActions();
	const { formatMessage } = useIntl();
	const [isBannerEnabled, setIsBannerEnabled] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [autoProjectCreationFailed, setAutoProjectCreationFailed] = useState(false);
	const [autoProjectCreationSuccess, setAutoProjectCreationSuccess] = useState(false);
	const [serviceDeskId, setServiceDeskId] = useState(0);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isModalOpenRef = useRef(false);
	const cloudId = useCloudId();
	const {
		data: { user: currentUser },
	} = useCurrentUser();

	// TODO add logic to add silverthrone for checking modal needs to be displayed or not
	const onBannerDismissClick = () => {
		setIsBannerEnabled(false);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'dismissed',
				actionSubject: 'topBanner',
				actionSubjectId: 'projectAutoCreateMessage',
			}),
		);
		markItemCompleted(cloudId, SERVICE_DESK_AUTO_PROJECT_CREATE_BANNER).catch(
			(error: Error | FetchError) => {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'markItemCompleted',
						packageName: 'jiraProjectsDirectoryV3',
						teamName: 'jira-cosmos',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		);
	};

	const openModal = useCallback(() => {
		setIsModalOpen(true);
		setIsLoading(true);
		isModalOpenRef.current = true;

		const analyticsEvent = createAnalyticsEvent({
			action: 'viewed',
			actionSubject: 'modal',
		});
		fireUIAnalytics(analyticsEvent, 'autoCreateProjectModal');

		createFirstJSMProject(currentUser.accountId ?? '')
			.then((response: CreateJSMProjectResponseType) => {
				setServiceDeskId(response?.id);
				if (isModalOpenRef.current) {
					push(
						`/jira/servicedesk/projects/${response?.id.toString()}/getting-started?referrer=${AUTO_CREATE_PROJECT_MODAL_REFERRER_VALUE}`,
					);
				} else {
					setAutoProjectCreationSuccess(true);
				}
				setIsLoading(false);
				setIsModalOpen(false);
				setIsBannerEnabled(false);
				fireUIAnalytics(analyticsEvent, 'modal closed', 'jsmProjectAutoCreatedSuccessfully');
				markItemCompleted(cloudId, SERVICE_DESK_AUTO_PROJECT_CREATE_BANNER).catch(
					(error: Error | FetchError) => {
						fireErrorAnalytics({
							error,
							meta: {
								id: 'markItemCompleted',
								packageName: 'jiraProjectsDirectoryV3',
								teamName: 'jira-cosmos',
							},
							sendToPrivacyUnsafeSplunk: true,
						});
					},
				);
			})
			.catch((_) => {
				setIsModalOpen(false);
				setIsLoading(false);
				setAutoProjectCreationFailed(true);
				fireUIAnalytics(analyticsEvent, 'modal closed', 'jsmProjectAutoCreationFailed');
			});
	}, [cloudId, createAnalyticsEvent, currentUser.accountId, push]);

	const handleAutoProjectCreationFailedDismiss = () => {
		setAutoProjectCreationFailed(false);
	};

	const handleAutoProjectCreationSuccessDismiss = () => {
		setAutoProjectCreationSuccess(false);
	};

	const handleOnClickTryAgain = () => {
		setAutoProjectCreationFailed(false);
		openModal();
	};

	const handleOnClickGoToYourServiceProject = () => {
		setServiceDeskId(0);
		setAutoProjectCreationSuccess(false);
		push(
			`/jira/servicedesk/projects/${serviceDeskId}/getting-started?referrer=${AUTO_CREATE_PROJECT_MODAL_REFERRER_VALUE}`,
		);
	};

	useEffect(() => {
		getCompletedItem(cloudId)
			.then((response) => {
				if (!response?.completedItems.includes(SERVICE_DESK_AUTO_PROJECT_CREATE_BANNER)) {
					setIsBannerEnabled(true);
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'viewed',
							actionSubject: 'topBanner',
							actionSubjectId: 'projectAutoCreateMessage',
						}),
					);
				}
			})
			.catch((error: Error | FetchError) => {
				// if we receive a 404 error, we create a new completed item
				// list for the user
				if (error instanceof FetchError && error.statusCode === ERROR_CODE_NOT_FOUND) {
					addList(cloudId).then((_) => {
						setIsBannerEnabled(true);
						fireUIAnalytics(
							createAnalyticsEvent({
								action: 'viewed',
								actionSubject: 'topBanner',
								actionSubjectId: 'projectAutoCreateMessage',
							}),
						);
					});
				}
				fireErrorAnalytics({
					error,
					meta: {
						id: 'getCompletedList',
						packageName: 'jiraProjectsDirectoryV3',
						teamName: 'jira-cosmos',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			});
	}, [cloudId, createAnalyticsEvent]);

	return (
		<>
			<FlagGroup onDismissed={handleAutoProjectCreationFailedDismiss}>
				{autoProjectCreationFailed && (
					<AkAutoDismissFlag
						id="auto-create-project-failed-flag"
						testId="global-pages.directories.projects-directory-v3.ui.page-layout.top-banner.auto-create-project-failed-flag"
						icon={
							<CrossCircleIcon
								primaryColor={token('color.icon.danger', R300)}
								label="Failure"
								size="medium"
							/>
						}
						title={formatMessage(messages.errorMessageTitle)}
						actions={[
							{
								content: formatMessage(messages.tryAgainLabel),
								onClick: handleOnClickTryAgain,
							},
						]}
					/>
				)}
			</FlagGroup>
			<FlagGroup onDismissed={handleAutoProjectCreationSuccessDismiss}>
				{autoProjectCreationSuccess && (
					<AkAutoDismissFlag
						id="auto-create-project-success-flag"
						testId="global-pages.directories.projects-directory-v3.ui.page-layout.top-banner.auto-create-project-success-flag"
						icon={
							<CheckCircleIcon
								primaryColor={token('color.icon.success', R300)}
								label="Success"
								size="medium"
							/>
						}
						title={formatMessage(messages.successMessageTitle)}
						description={formatMessage(messages.successMessageDescription)}
						actions={[
							{
								content: formatMessage(messages.successMessageLink),
								onClick: handleOnClickGoToYourServiceProject,
							},
						]}
					/>
				)}
			</FlagGroup>
			{isBannerEnabled && (
				<Box xcss={SectionMessageStyles}>
					<SectionMessage
						appearance="discovery"
						title={formatMessage(messages.title)}
						icon={LightbulbIcon}
						actions={[
							<SectionMessageAction
								key="create-service-project"
								testId="global-pages.directories.projects-directory-v3.ui.page-layout.top-banner.primary-button"
								onClick={openModal}
							>
								{formatMessage(messages.primaryAction)}
							</SectionMessageAction>,
							<SectionMessageAction
								key="dismiss"
								testId="global-pages.directories.projects-directory-v3.ui.page-layout.top-banner.secondary-button"
								onClick={onBannerDismissClick}
							>
								{formatMessage(messages.secondaryAction)}
							</SectionMessageAction>,
						]}
						testId="global-pages.directories.projects-directory-v3.ui.page-layout.top-banner.section-message"
					>
						<p>
							{formatMessage(messages.description, {
								l: (chunks: ReactNode) => (
									<a
										href="https://support.atlassian.com/jira-service-management-cloud/docs/what-is-jira-service-management/"
										target="_blank"
										onClick={(e: SyntheticEvent) => {
											e.stopPropagation();
											fireUIAnalytics(
												createAnalyticsEvent({
													action: 'clicked',
													actionSubject: 'link',
													actionSubjectId: 'projectAutoCreateDescription',
												}),
											);
										}}
									>
										{chunks}
									</a>
								),
							})}
						</p>
					</SectionMessage>
				</Box>
			)}
			<AutoCreateProjectModal
				isModalOpen={isModalOpen}
				setIsOpen={setIsModalOpen}
				isLoading={isLoading}
				isModalOpenRef={isModalOpenRef}
			/>
		</>
	);
};

const SectionMessageStyles = xcss({
	position: 'relative',
	marginTop: 'space.200',
});
